import { logException } from '../../common/utils/sentry';
import storage from '../../common/utils/storage';

import { DEFAULT_FEATURE_FLAGS, PRODUCT_FEATURE_FLAGS } from './constants';
import { type FeatureFlag, type FeatureFlagResult, type ProductFeatureFlag } from './types';

const STORAGE_KEY_SETTINGS = 'atlassian.ptc-directory.settings';
/**
 * Caches settings in local storage
 */
export function putSettingsInStorage(settings: FeatureFlagResult): void {
	storage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(settings));
	updateEffectiveFlags(settings);
}

/**
 * Retrieve a map of the settings from local storage.  Returns null if no settings are found.
 * Is only a partial result, as flags may have changed since they were saved in local storage.
 */
export function getSettingsFromStorage(): Partial<FeatureFlagResult> | null {
	const settingsString = storage.getItem(STORAGE_KEY_SETTINGS);
	try {
		const parsed = settingsString ? JSON.parse(settingsString) : null;
		if (isStringBooleanDictionary(parsed)) {
			return parsed;
		}
		return null;
	} catch (e) {
		logException(e, 'Could not get settings from storage', {
			locallyStoredSettings: settingsString,
		});
		return null;
	}
}

const effectiveFlags: FeatureFlagResult = {
	...DEFAULT_FEATURE_FLAGS,
	...(getSettingsFromStorage() || {}),
};

export function updateEffectiveFlags(newFlags: FeatureFlagResult & Record<string, boolean>) {
	Object.assign(effectiveFlags, newFlags);
}

/**
 * We use this function to validate that the settings retrieved from local storage are of the correct shape.
 * We intentionally don't check against explicit keys, as keys may have changed since being stored.
 */
function isStringBooleanDictionary(result: any): result is Record<string, boolean> {
	return (
		result !== null &&
		Object.keys(result).every(
			(resultKey) => typeof resultKey === 'string' && typeof result[resultKey] === 'boolean',
		)
	);
}

export function isProductFeatureFlag(featureFlag: FeatureFlag): featureFlag is ProductFeatureFlag {
	return Object.keys(PRODUCT_FEATURE_FLAGS).includes(featureFlag);
}

/**
 * @deprecated
 * Please use useFeatureFlag or useBooleanFeatureFlag
 * This method return best known flag value, but only compute values from Directory GraphQL,
 * local storage, and default values.
 *
 * Using this in React components should be avoided, as the component won't re-render when
 * feature flag values are loaded.
 *
 * Keeping for legacy usage only (it is exported from `@atlassian/ptc-common`)
 */
export function isFlagEnabled(flag: FeatureFlag) {
	if (process.env.NODE_ENV === 'development') {
		// eslint-disable-next-line no-console
		console.warn('isFlagEnabled is deprecated, please use useFeatureFlag or useBooleanFeatureFlag');
	}
	return effectiveFlags[flag];
}
